import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";

import { softwareDevelopmentData } from "../../../../data/SoftwareDevelopmentData";
import ContactModal from "../../../../Assets/Common/ContactModal";

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 18,
  },

  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const CTASection = () => {
  const { stats } = softwareDevelopmentData;

  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const handleGetStarted = () => {
    setIsContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setIsContactModalOpen(false);
  };

  const fallbackStats = [
    {
      value: "50+",
      label: "Projects",
    },
    {
      value: "30+",
      label: "Solutions",
    },
    {
      value: "100%",
      label: "Scalable",
    },
    {
      value: "24/7",
      label: "Support",
    },
  ];

  const displayStats = stats?.length ? stats.slice(0, 4) : fallbackStats;

  return (
    <>
      <section
        className="
          relative
          isolate
          w-full
          overflow-hidden
          bg-[var(--bg-primary)]
          py-6
          sm:py-8
          md:py-10
          lg:py-12
          xl:py-14
        "
      >
        <div
          className="
            pointer-events-none
            absolute
            left-1/2
            top-1/2
            -z-10
            h-56
            w-56
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            bg-orange-500/[0.055]
            blur-[90px]
            sm:h-72
            sm:w-72
            sm:blur-[105px]
            md:h-96
            md:w-96
            md:blur-[120px]
          "
        />

        <div
          className="
            pointer-events-none
            absolute
            -left-24
            bottom-0
            -z-10
            h-40
            w-40
            rounded-full
            bg-orange-500/[0.03]
            blur-[75px]
            sm:-left-32
            sm:h-56
            sm:w-56
            sm:blur-[90px]
            md:h-72
            md:w-72
            md:blur-[105px]
          "
        />

        <div
          className="
            pointer-events-none
            absolute
            -right-24
            top-0
            -z-10
            h-40
            w-40
            rounded-full
            bg-orange-500/[0.03]
            blur-[75px]
            sm:-right-32
            sm:h-56
            sm:w-56
            sm:blur-[90px]
            md:h-72
            md:w-72
            md:blur-[105px]
          "
        />

        <div
          className="
            relative
            mx-auto
            w-full
            max-w-7xl
            px-3
            sm:px-5
            md:px-6
            lg:px-8
          "
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 25,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.15,
            }}
            transition={{
              duration: 0.65,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="
              relative
              w-full
              overflow-hidden
              rounded-2xl
              border
              border-orange-500/15
              bg-[var(--card-bg-secondary)]
              px-4
              py-7
              shadow-[0_20px_70px_var(--shadow-color)]
              sm:rounded-3xl
              sm:px-6
              sm:py-8
              md:px-8
              md:py-10
              lg:px-10
              lg:py-11
              xl:px-12
              xl:py-12
            "
          >
            <div
              className="
                pointer-events-none
                absolute
                left-1/2
                top-0
                h-px
                w-[65%]
                -translate-x-1/2
                bg-gradient-to-r
                from-transparent
                via-orange-500/40
                to-transparent
                blur-[1px]
              "
            />

            <div
              className="
                pointer-events-none
                absolute
                -right-20
                -top-20
                h-40
                w-40
                rounded-full
                bg-orange-500/[0.055]
                blur-[70px]
                sm:-right-24
                sm:-top-24
                sm:h-56
                sm:w-56
                sm:blur-[85px]
                md:h-64
                md:w-64
              "
            />

            <div
              className="
                pointer-events-none
                absolute
                -bottom-20
                -left-20
                h-40
                w-40
                rounded-full
                bg-orange-500/[0.03]
                blur-[70px]
                sm:-bottom-24
                sm:-left-24
                sm:h-56
                sm:w-56
                sm:blur-[85px]
              "
            />

            <div
              className="
                pointer-events-none
                absolute
                inset-0
                opacity-[0.018]
                [background-image:linear-gradient(rgba(128,128,128,.5)_1px,transparent_1px),linear-gradient(90deg,rgba(128,128,128,.5)_1px,transparent_1px)]
                [background-size:42px_42px]
                sm:[background-size:52px_52px]
              "
            />

            <div
              className="
                relative
                z-10
                flex
                w-full
                flex-col
                items-center
                gap-7
                sm:gap-8
                md:gap-9
                lg:flex-row
                lg:items-center
                lg:justify-between
                lg:gap-10
                xl:gap-14
              "
            >
              <div
                className="
                  w-full
                  max-w-3xl
                  text-center
                  lg:flex-1
                  lg:text-left
                "
              >
                <motion.span
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{
                    once: true,
                    amount: 0.2,
                  }}
                  className="
                    inline-flex
                    max-w-full
                    items-center
                    justify-center
                    rounded-full
                    border
                    border-orange-500/20
                    bg-orange-500/[0.07]
                    px-2.5
                    py-1.5
                    text-[8px]
                    font-semibold
                    uppercase
                    tracking-[0.16em]
                    text-[var(--brand-light)]
                    sm:px-3
                    sm:py-1.5
                    sm:text-[9px]
                    sm:tracking-[0.2em]
                    md:px-3.5
                    md:py-2
                    md:text-[10px]
                  "
                >
                  Let's Build Together
                </motion.span>

                <motion.h2
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{
                    once: true,
                    amount: 0.2,
                  }}
                  className="
                    mx-auto
                    mt-3
                    max-w-[620px]
                    text-[25px]
                    font-bold
                    leading-[1.12]
                    tracking-[-0.025em]
                    text-[var(--text-primary)]
                    sm:mt-4
                    sm:text-3xl
                    md:text-4xl
                    md:leading-[1.1]
                    lg:mx-0
                    lg:mt-4
                    lg:max-w-[650px]
                    lg:text-[42px]
                    xl:text-[46px]
                  "
                >
                  Ready to Build Your{" "}
                  <span className="text-[var(--brand-primary)]">
                    Next Software Solution?
                  </span>
                </motion.h2>

                <motion.p
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{
                    once: true,
                    amount: 0.2,
                  }}
                  className="
                    mx-auto
                    mt-3
                    max-w-[620px]
                    text-[11px]
                    leading-[1.75]
                    text-[var(--text-secondary)]
                    sm:mt-4
                    sm:text-xs
                    sm:leading-6
                    md:text-sm
                    md:leading-7
                    lg:mx-0
                    lg:mt-4
                    lg:max-w-[610px]
                    lg:text-[15px]
                    xl:text-base
                  "
                >
                  Whether you have a clear product idea or a business
                  challenge, our team can help transform your requirements
                  into a reliable, scalable, and user-focused software
                  solution.
                </motion.p>

                <motion.div
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{
                    once: true,
                    amount: 0.2,
                  }}
                  className="
                    mt-5
                    flex
                    w-full
                    justify-center
                    sm:mt-6
                    lg:w-auto
                    lg:justify-start
                  "
                >
                  <button
                    type="button"
                    onClick={handleGetStarted}
                    className="
                      group
                      inline-flex
                      w-full
                      max-w-[260px]
                      items-center
                      justify-center
                      gap-2
                      rounded-xl
                      bg-[var(--brand-primary)]
                      px-5
                      py-3
                      text-xs
                      font-semibold
                      text-white
                      shadow-[0_8px_25px_rgba(249,115,22,0.12)]
                      transition-all
                      duration-300
                      hover:-translate-y-0.5
                      hover:bg-[var(--brand-hover)]
                      hover:shadow-[0_12px_32px_rgba(249,115,22,0.2)]
                      active:translate-y-0
                      sm:max-w-none
                      sm:w-auto
                      sm:px-6
                      sm:py-3.5
                      sm:text-sm
                      md:text-[15px]
                    "
                  >
                    <span>Get Started</span>

                    <span
                      className="
                        flex
                        h-5
                        w-5
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        bg-white/10
                        transition-transform
                        duration-300
                        group-hover:translate-x-1
                        sm:h-6
                        sm:w-6
                      "
                    >
                      <ArrowRight
                        size={13}
                        strokeWidth={2.2}
                        className="sm:h-[14px] sm:w-[14px]"
                      />
                    </span>
                  </button>
                </motion.div>
              </div>

              <div
                className="
                  grid
                  w-full
                  max-w-[360px]
                  grid-cols-2
                  gap-2.5
                  sm:max-w-[390px]
                  sm:gap-3
                  md:max-w-[420px]
                  md:gap-3.5
                  lg:w-[280px]
                  lg:max-w-[280px]
                  lg:shrink-0
                  lg:grid-cols-2
                  xl:w-[310px]
                  xl:max-w-[310px]
                "
              >
                {displayStats.map((item, index) => (
                  <motion.div
                    key={`${item.label}-${index}`}
                    initial={{
                      opacity: 0,
                      y: 15,
                    }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                    }}
                    viewport={{
                      once: true,
                      amount: 0.2,
                    }}
                    transition={{
                      duration: 0.45,
                      delay: 0.12 + index * 0.06,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    whileHover={{
                      y: -3,
                    }}
                    className="
                      group
                      relative
                      min-w-0
                      overflow-hidden
                      rounded-xl
                      border
                      border-[var(--border-primary)]
                      bg-[var(--card-bg-secondary)]
                      px-2.5
                      py-3.5
                      text-center
                      transition-all
                      duration-300
                      hover:border-orange-500/25
                      hover:bg-orange-500/[0.035]
                      sm:rounded-2xl
                      sm:px-3
                      sm:py-4
                      md:px-4
                      md:py-5
                    "
                  >
                    <div
                      className="
                        pointer-events-none
                        absolute
                        left-1/2
                        top-0
                        h-12
                        w-16
                        -translate-x-1/2
                        rounded-full
                        bg-orange-500/0
                        blur-[25px]
                        transition-all
                        duration-500
                        group-hover:bg-orange-500/[0.12]
                        sm:h-16
                        sm:w-20
                      "
                    />

                    <h3
                      className="
                        relative
                        text-xl
                        font-bold
                        leading-none
                        tracking-tight
                        text-[var(--brand-primary)]
                        sm:text-2xl
                        md:text-[26px]
                      "
                    >
                      {item.value}
                    </h3>

                    <p
                      className="
                        relative
                        mt-1.5
                        truncate
                        text-[9px]
                        font-medium
                        text-[var(--text-muted)]
                        sm:mt-2
                        sm:text-[10px]
                        md:text-xs
                      "
                    >
                      {item.label}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            <div
              className="
                pointer-events-none
                absolute
                bottom-0
                left-[10%]
                right-[10%]
                h-px
                bg-gradient-to-r
                from-transparent
                via-orange-500/10
                to-transparent
              "
            />
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {isContactModalOpen && (
          <ContactModal
            isOpen={isContactModalOpen}
            onClose={handleCloseContactModal}
            initialService="Software Development"
            initialSubService=""
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default CTASection;